/**
 * Unit.ts
 * Moveable combatant. Extends Entity with grid position, HP, class identity,
 * selection state, and smooth tile-to-tile movement.
 *
 * Movement model:
 *   - `gridPos`  — logical tile the unit currently occupies (gameplay)
 *   - `path`     — remaining tiles to walk through
 *   - `moveT`    — 0..1 progress toward path[0]; used by UnitRenderer to lerp
 *
 * Game.ts passes a tempo-scaled delta to update(), so the unit itself doesn't
 * need to know about the RhythmSystem.
 */

import { Entity } from './Entity';
import type { BandClass, CombatOrder, CombatStats, EnemyType, GridPosition } from '../types';

export type SpecialUnitRole =
  | 'streamingMonolith'
  | 'baddieBeacon'
  | 'corporateHQ'
  | 'rivalVenueBuilder'
  | 'rivalVenue';

export class Unit extends Entity {
  /** Logical tile — where the unit "is" for gameplay (combat range, fog, etc). */
  gridPos: GridPosition = { col: 0, row: 0 };

  /** Full combat stat block — populated by the system that creates this unit. */
  stats: CombatStats | null = null;

  /** Remaining path tiles. path[0] is the next tile to step into. */
  path: GridPosition[] = [];

  /** 0..1 fractional progress toward path[0]. Used for smooth rendering. */
  moveT = 0;

  /**
   * Seconds this unit has spent unable to enter path[0] because the tile is
   * occupied.  Once it exceeds BLOCKED_ABANDON_SECS the path is cleared so the
   * separation system can reroute rather than leaving the unit frozen at 0.95.
   */
  private blockedTimer = 0;
  private static readonly BLOCKED_ABANDON_SECS = 0.08;

  /** True while the unit is in the player's selection set. */
  selected = false;

  // ── Health ────────────────────────────────────────────────────────────
  maxHp = 100;
  hp    = 100;
  /** Band members do not die at 0 HP; they are downed until revived. */
  isDowned = false;
  /** 0..1 progress while another band member revives this unit. */
  reviveProgress = 0;

  // ── Identity ──────────────────────────────────────────────────────────
  /** BandClass for player units; null for enemy/neutral units. */
  bandClass: BandClass | null = null;

  /** EnemyType for scene-killer units; null for player units. */
  enemyType: EnemyType | null = null;

  /** Hero units are band leaders — one per class, extra visual treatment. */
  isHero = false;

  /** Human-readable label shown in the selection panel. */
  displayName = 'Unit';
  /** Visual/threat multiplier for special wave units. 1 = normal. */
  threatScale = 1;
  /** Special scripted role for objective enemies and destructible enemy structures. */
  specialRole: SpecialUnitRole | null = null;
  /** Objective tile used by scripted enemy roles such as the Streaming Monolith. */
  objectiveTarget: GridPosition | null = null;
  /** 0..1 construction progress for destructible enemy structures. */
  constructionProgress = 0;
  /** True once a constructed enemy objective starts applying its disruption. */
  constructionComplete = false;
  /** True while a unit is channeling a construction objective. */
  isConstructing = false;
  /** ID link for scripted relationships such as a beacon's builder. */
  linkedUnitId: string | null = null;
  /** Seconds until a completed enemy structure can spawn its next support group. */
  spawnTimer = 0;
  /** Rival Venue: current escalation level, 1-3. */
  rivalVenueLevel = 1;
  /** Rival Venue: seconds since opening night. */
  rivalVenueAge = 0;
  /** Rival Venue: next age threshold that will trigger a level-up. */
  rivalVenueNextEscalation = 0;

  // ── Movement ─────────────────────────────────────────────────────────
  /**
   * Tiles per second at normal tempo.
   * Game.ts scales delta by tempoModifier before calling update(), so the
   * unit just uses this value directly.
   */
  moveSpeed = 3.0;

  // ── Combat ────────────────────────────────────────────────────────────
  /** Damage dealt per successful hit. */
  attackDamage   = 20;
  /** Attacks per second. */
  attackRate     = 1.0;
  /** Tiles — enemies within this radius enter the aggro / chase range. */
  sightRadius    = 5;
  /** Tiles — attack fires when target is closer than this value. */
  attackRadius   = 1.5;
  /** Countdown (s) until the next attack can fire. Resets to 1/attackRate after each swing. */
  attackCooldown = 0;
  /** Seconds remaining on the red hit-flash tint drawn by UnitRenderer. */
  hitFlash       = 0;
  /** Seconds remaining on the orange attack pulse drawn around the attacker. */
  attackPulse    = 0;
  /** Current attack target; null when idle or when target has died. */
  target: Unit | null = null;
  /** Player/enemy intent used by CombatSystem to decide whether to chase, retarget, or hold. */
  combatOrder: CombatOrder = 'idle';
  /** Destination associated with move/attack-move/defend orders. */
  orderTarget: GridPosition | null = null;
  /** Specific target associated with direct attack orders. */
  orderTargetUnit: Unit | null = null;
  /** Music Press starts neutral; at 0 it becomes hostile if not converted. */
  pressHostileTimer: number | null = null;

  /** True when this fan unit is actively mining a Gear Depot. Set by GearSystem. */
  isMining = false;

  // ── Song Drop status effects ──────────────────────────────────────────
  /** Seconds remaining on the anthem speed buff (Song Drop effect). */
  anthemBuff = 0;
  /** Seconds remaining on stun; movement is blocked while > 0 (Song Drop or Power Chord). */
  stunTimer  = 0;

  // ── Band member active skills ─────────────────────────────────────────
  /** Seconds until this unit's skill can fire again. 0 = ready. */
  skillCooldown    = 0;
  /** Seconds remaining on the Crowd Hype movement speed boost. */
  speedBoostTimer  = 0;
  /** Seconds remaining on the Tempo Surge attack rate boost. */
  attackBoostTimer = 0;

  // ── Jim's spell effects ───────────────────────────────────────────────
  /** Temporal Echo: seconds remaining at 50% movement speed (also used by Mogul aura on player units). */
  moveSlowTimer = 0;
  /** Crowd Confusion: seconds remaining attacking own faction (also used by BadManager corruption). */
  confusedTimer = 0;

  // ── Codex AI director state ───────────────────────────────────────────
  /** BattlePlanner-issued march target; overrides default objective when set. */
  commandTarget:  GridPosition | null = null;
  /** BattlePlanner has ordered this unit to retreat toward the nearest map edge. */
  isRetreating:   boolean = false;

  // ── Enemy unit-type behaviors ─────────────────────────────────────────
  /** StreamingMogul: seconds until next interference aura pulse (starts at 0 → first pulse on spawn). */
  auraTimer:       number = 0;
  /** GenZSwarm: seconds remaining on current burst sprint. */
  burstTimer:      number = 0;
  /** GenZSwarm: seconds until next burst cycle triggers. */
  burstCycleTimer: number = 0;
  /** TributeAct: true once the unit has confirmed enough nearby allies to begin its charge. */
  rallyReady:      boolean = false;

  // ── Entity contract ───────────────────────────────────────────────────

  /**
   * Advance movement along the current path.
   * delta should already be multiplied by tempoModifier by the caller.
   */
  update(delta: number, isBlocked?: (pos: GridPosition) => boolean): void {
    if (this.isDowned) {
      this.path         = [];
      this.moveT        = 0;
      this.blockedTimer = 0;
      return;
    }

    if (this.path.length === 0) {
      this.moveT        = 0;
      this.blockedTimer = 0;
      return;
    }

    this.moveT += this.moveSpeed * delta;

    // Consume path steps — handles cases where moveSpeed * delta > 1 tile
    while (this.moveT >= 1 && this.path.length > 0) {
      const next = this.path[0];
      if (isBlocked?.(next)) {
        this.blockedTimer += delta;
        if (this.blockedTimer >= Unit.BLOCKED_ABANDON_SECS) {
          // Stuck too long against a blocked tile — abandon the path so the
          // separation system can reroute rather than leaving this unit frozen.
          this.path         = [];
          this.moveT        = 0;
          this.blockedTimer = 0;
        } else {
          this.moveT = 0.95;
        }
        return;
      }
      // Successfully stepping into the next tile — reset the blocked counter.
      this.blockedTimer = 0;
      this.moveT -= 1;
      this.gridPos = { col: next.col, row: next.row };
      this.path.shift();
    }

    if (this.path.length === 0) this.moveT = 0;
  }

  /** Assign a new path (replaces any existing movement). */
  setPath(path: GridPosition[]): void {
    if (this.isDowned) return;
    this.path  = path;
    this.moveT = 0;
  }

  issueMove(path: GridPosition[], target?: GridPosition): void {
    this.setPath(path);
    this.combatOrder = 'move';
    this.orderTarget = target ? { ...target } : null;
    this.orderTargetUnit = null;
    this.target = null;
  }

  issueAttackMove(path: GridPosition[], target: GridPosition): void {
    this.setPath(path);
    this.combatOrder = 'attackMove';
    this.orderTarget = { ...target };
    this.orderTargetUnit = null;
    this.target = null;
  }

  issueAttackTarget(target: Unit): void {
    if (this.isDowned) return;
    this.combatOrder = 'attackTarget';
    this.orderTarget = { ...target.gridPos };
    this.orderTargetUnit = target;
    this.target = target;
  }

  clearCombatOrder(): void {
    this.combatOrder = 'idle';
    this.orderTarget = null;
    this.orderTargetUnit = null;
    this.target = null;
  }

  /** True when the unit has tiles remaining to walk. */
  get isMoving(): boolean {
    return this.path.length > 0;
  }

  override destroy(): void {
    super.destroy();
    this.selected = false;
    this.path     = [];
    this.moveT    = 0;
    this.attackPulse = 0;
    this.target      = null;
    this.combatOrder = 'idle';
    this.orderTarget = null;
    this.orderTargetUnit = null;
    this.pressHostileTimer = null;
    this.threatScale = 1;
    this.specialRole = null;
    this.objectiveTarget = null;
    this.constructionProgress = 0;
    this.constructionComplete = false;
    this.isConstructing = false;
    this.linkedUnitId = null;
    this.anthemBuff      = 0;
    this.stunTimer       = 0;
    this.skillCooldown   = 0;
    this.speedBoostTimer = 0;
    this.attackBoostTimer = 0;
    this.moveSlowTimer   = 0;
    this.confusedTimer   = 0;
    this.commandTarget   = null;
    this.isRetreating    = false;
    this.auraTimer       = 0;
    this.burstTimer      = 0;
    this.burstCycleTimer = 0;
    this.rallyReady      = false;
    this.isDowned        = false;
    this.reviveProgress  = 0;
  }
}
